<template>
  <ModalDialog
    ref="dialog"
    title="Select Glass Sheet For Cutting"
    icon="fa-layer-group"
    :showFooter="true"
    :submitDisabled="!hasSelectedGlass"
    :submitting="submitting"
    submitTitle="Cut Glass"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <LoadingPanel :loadingError="binLoadingError" />
    <FormGroup v-bind:form="form" :labelColumnSize="3" v-show="!loading && !loadingError">
      <ContentTitle>{{stockData.code}} - {{stockData.name}}</ContentTitle>

      <FormRow label="Sheet Size">
        <FormSelect :options="glassLevelLookup" v-model="selectedGlassLevelId"></FormSelect>
      </FormRow>

      <FormRow label="Source Inventory Bin" v-show="!isOffcutSelected">
        <FormInventoryBinSelect
          v-model="sourceInventoryBinId"
          placeholder="Inventory Bin"
          :accountLocationId="productionStation.accountLocationId" 
          :defaultLabelType="InventoryLabelTypes.defaultProductionStock"
          :note="inventoryBinNote"
        />
      </FormRow>

      <FormRow label="Quantity" v-if="!isOffcutSelected">
        <FormInputText type="number" v-model="quantity" field="quantity" placeholder="Quantity"></FormInputText>
      </FormRow>
      <FormRow v-if="hasSelectedGlass && !hasSheetsAvailable">
        <FormStaticLabel><i class="fas fa-exclamation-triangle text-danger" /> Insufficient sheets available</FormStaticLabel>
      </FormRow>
      <FormRow v-if="hasSelectedBin && !hasInventoryBinSheetsAvailable && hasSheetsAvailable && !loadingBinContents">
        <FormStaticLabel><i class="fas fa-exclamation-triangle text-danger" /> Insufficient sheets available in inventory bin</FormStaticLabel>
      </FormRow>

      <FormRow label="Offcut Barcode" v-if="isOffcutSelected">
        <FormStaticLabel>{{selectedGlass.barcode}}</FormStaticLabel>
      </FormRow>
            
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'

import FormInventoryBinSelect from '@/modules/inventory/components/formInventoryBinSelect'

import { InventoryLabelTypes, InventoryBinLinkTypes, LinkTypes } from '@/store/types'

export default {
  name: 'GlassSheetCuttingDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog,
    FormInventoryBinSelect
  },
  props: {
    stockId: Number,
    glassLevelId: Number,
    productionStageId: Number,
    productionStationId: Number
  },
  watch: {
    sourceInventoryBinId: function() {
      // Request inventory breakdown to get stock in the bin
      this.requestBinContents()
    }
  },
  data() {
    return {
      loading: false,
      loadingError: '',
      binLoadingError: '',
      glassLevelLookup: [],
      productionStation: {},

      stockData: { },
      selectedGlassLevelId: null,
      sourceInventoryBinId: null,
      
      loadingBinContents: false,
      inventoryBinContents: [],
      quantity: 1,
      form: {},
      submitting: false
    }
  },
  created() {
    this.InventoryLabelTypes = InventoryLabelTypes
  },
  mounted() {
  },
  computed: {
    selectedGlass() {
      return this.glassLevelLookup.reduce((curLevel, glassSize) => {
        if(curLevel)
          return curLevel
        return glassSize.children.find(glassLevel => glassLevel.id == this.selectedGlassLevelId)
      }, null)
    },
    hasSelectedGlass() {
      return this.selectedGlassLevelId != null
    },
    hasSelectedBin() {
      return !!this.sourceInventoryBinId
    },
    isOffcutSelected() {
      if(this.selectedGlass)
        return this.selectedGlass.isOffcut
      return false
    },
    hasSheetsAvailable() {
      if(this.isOffcutSelected)
        return true

      if(this.selectedGlass && this.selectedGlass.qtyOnHand >= parseInt(this.quantity || 0))
        return true
      return false
    },
    hasInventoryBinSheetsAvailable() {
      if(this.isOffcutSelected)
        return true

      return this.inventoryBinSheetQuantity >= parseInt(this.quantity || 0)
    },
    inventoryBinSheetQuantity() {
      // Calculate total loose sheets quantities / glass block
      // Offcut ignored as it has a fixed inventory bin id
      var quantity = 0
      this.inventoryBinContents.forEach(contentInfo => {
        // Loose sheets
        if(contentInfo.linkType == LinkTypes.inventoryBinLink) {
          if(contentInfo.data.glassLevelId == this.selectedGlassLevelId && contentInfo.data.type == InventoryBinLinkTypes.glassLevel) {
            quantity += contentInfo.data.quantity
          }
        }
      })

      return quantity
    },
    inventoryBinNote() {
      if(this.hasSelectedGlass && this.selectedGlass && !this.isOffcutSelected && !this.loadingBinContents) {
        return `${this.inventoryBinSheetQuantity} available sheet(s) of ${this.selectedGlass.height}x${this.selectedGlass.width} in inventory bin`
      }
      return ''
    }
  },
  methods: {
    show() {
      this.form = {}

      this.submitting = false
      this.stockData = { }
      this.glassLevelLookup = []

      // Download the stock / glass data
      this.requestStockData()

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      // Send cut glass level request
      this.form = this.clearFormResponse()

      // If an offcut, change source bin to match offcut
      if(this.isOffcutSelected && this.selectedGlass.inventoryBin) {
        this.sourceInventoryBinId = this.selectedGlass.inventoryBin.id
        this.quantity = 1
      }

      this.submitting = true
      this.$apiService.glassProductionStationService.cutGlassLevel(this.productionStationId, this.selectedGlassLevelId, { quantity: this.quantity, inventoryBinId: this.sourceInventoryBinId })
      .then(response => {
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.submitting = false
      })
    },
    requestStockData() {
      this.loading = true

      var requestList = [
        this.$apiService.inventoryService.getStock(this.stockId),
        this.$apiService.productionService.getProductionStation(this.productionStationId)
      ]
      if(this.glassLevelId)
        requestList.push(this.$apiService.inventoryService.getGlassLevel(this.glassLevelId))

      Promise.all(requestList)
      .then(results => {
        this.stockData = results[0].data
        this.productionStation = results[1].data

        // Build the list of glass levels
        // Build the glass size list
        this.glassLevelLookup = []
        this.glassLevelLookup = this.stockData.glassSizeList.reduce((list, glassSize) => {
          if(!glassSize.isActive || glassSize.glassLevels.length == 0)
            return list
          list.push({
            name: `${glassSize.height}x${glassSize.width}`,
            children: glassSize.glassLevels.filter(glassLevel => glassLevel.isActive && (!glassLevel.isOffcut || glassLevel.isOffcutAvailable)).map(glassLevel => {
              let levelName = `${glassLevel.height} x ${glassLevel.width}`
              if(glassLevel.isOffcut)
                levelName += ` - Offcut`
              else
                levelName += ` - ${glassLevel.qtyOnHand} Sheets`

              return {
                id: glassLevel.id,
                value: glassLevel.id,
                name: levelName,
                width: glassLevel.width,
                height: glassLevel.height,
                isOffcut: glassLevel.isOffcut,
                barcode: glassLevel.barcode,
                inventoryBin: glassLevel.inventoryBin,
                offcutComments: glassLevel.offcutComments,
                grainDirection: glassSize.grainDirection,
                qtyOnHand: glassLevel.qtyOnHand
              }
            })
          })
          return list
        }, [])
        
        if(this.glassLevelId) {
          this.selectedGlassLevelId = this.glassLevelId

          // If the glassLevelId was specified on the dialog (ie, scanned)
          // And if we've only got 1 possible inventory bin stock belongs to at this location, then just automatically scan the sheet off
          // OR it's an offcut
          let glassLevelData = results[2].data
          if(glassLevelData.inventoryBinLinks) {
            // Get bins at this location that contain this level
            let locationBins = glassLevelData.inventoryBinLinks
            .filter(invBinLink => invBinLink.inventoryBin.accountLocationId == this.productionStation.accountLocationId)
            .map(invBinLink => invBinLink.inventoryBin)

            // If only 1 bin, select it
            if(_.uniqBy(locationBins, 'id').length == 1) {
              this.sourceInventoryBinId = locationBins[0].id

              this.onSubmitted()
              return
            }            
          }

          // If an offcut, just automatically go ahead
          if(this.isOffcutSelected) {
            this.sourceInventoryBinId = this.selectedGlass.inventoryBin.id
            this.onSubmitted()
            return
          }

        } else {
          // Select 1st available glass level
          this.selectedGlassLevelId = this.glassLevelLookup.reduce((curId, glassSize) => {
            if(curId)
              return curId
            if(glassSize.children && glassSize.children.length > 0)
              return glassSize.children[0].value
            return 0
          }, 0)
        }

      })
      .catch(err => {
        console.error(err)
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
      })
    },

    requestBinContents() {
      if(!this.sourceInventoryBinId)
        return

      var params = {
        filter: {
          stockId: this.stockId
        }
      }

      this.loadingBinContents = true
      this.$apiService.inventoryService.getInventoryBinContents(this.sourceInventoryBinId, params)
      .then(response => {
        this.inventoryBinContents = response.data
      })
      .catch(err => {
        console.error(err)
        this.binLoadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loadingBinContents = false
      })
    },

    
 
  }
}
</script>